import React, { useState } from "react";
import "./Projects.css";

const ProjectGallery: React.FC<{ images: string[]; alt: string }> = ({ images, alt }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="project-gallery">
      <div className="gallery-image">
        <img src={images[currentImageIndex]} alt={alt} />
      </div>
      {images.length > 1 && (
        <>
          <button className="gallery-nav prev" onClick={prevImage} aria-label="Previous image">
            ‹
          </button>
          <button className="gallery-nav next" onClick={nextImage} aria-label="Next image">
            ›
          </button>
          <div className="gallery-indicators">
            {images.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => setCurrentImageIndex(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ImagePopup: React.FC<{ 
  images: string[]; 
  alt: string; 
  isOpen: boolean; 
  onClose: () => void;
  initialIndex?: number;
}> = ({ images, alt, isOpen, onClose, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  if (!isOpen) return null;

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="image-popup-overlay" onClick={onClose}>
      <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="image-popup-close" onClick={onClose} aria-label="Close popup">×</button>
        
        <div className="image-popup-main">
          <img src={images[currentIndex]} alt={`${alt} - Image ${currentIndex + 1}`} className="image-popup-image" />
          
          {images.length > 1 && (
            <>
              <button className="image-popup-nav prev" onClick={prevImage} aria-label="Previous image">
                ‹
              </button>
              <button className="image-popup-nav next" onClick={nextImage} aria-label="Next image">
                ›
              </button>
              
              <div className="image-popup-indicators">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`image-popup-indicator ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index)}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
              
              <div className="image-popup-counter">
                {currentIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Projects: React.FC = () => {
  const [popupImages, setPopupImages] = useState<string[]>([]);
  const [popupAlt, setPopupAlt] = useState<string>('');
  const [popupInitialIndex, setPopupInitialIndex] = useState<number>(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openImagePopup = (images: string[], alt: string, initialIndex: number = 0) => {
    setPopupImages(images);
    setPopupAlt(alt);
    setPopupInitialIndex(initialIndex);
    setIsPopupOpen(true);
  };

  const closeImagePopup = () => {
    setIsPopupOpen(false);
    setPopupImages([]);
    setPopupAlt('');
    setPopupInitialIndex(0);
  };

  const projects = [
    {
      title: "PINAC Workspace",
      description: "Privacy-first desktop AI chat app built with Electron + React UI and a powerful Python backend. Choose between local (Ollama) and cloud (Pinac-Cloud) models; offline-first with local chat history and optional local RAG, real-time web search, secure auth via Firebase, modern desktop UX.",
      tech: [
        "Electron",
        "React",
        "Python",
        "Firebase Auth",
        "RAG",
        "Ollama",
      ],
      image: "/pinac/image1.png",
      imageAlt: "PINAC Workspace screenshot",
      gallery: ["/pinac/image1.png", "/pinac/image2.png"],
      repo: "https://github.com/pinacai/PINAC_Workspace",
      website: "https://pinacworkspace.vercel.app/"
    },
    {
      title: "Hamro Class",
      description: "Syllabus tracker for classes to solve traditional syllabus completion problems. Tailored UIs for teacher, student, and college administration to plan, track, and review progress.",
      tech: ["React", "MongoDB", "Express", "Node.js"],
      image: "/hamroclass/image3.png",
      imageAlt: "Hamro Class preview",
      gallery: ["/hamroclass/image3.png", "/hamroclass/image1.png", "/hamroclass/image2.png", "/hamroclass/image.png"],
      repoFrontend: "https://github.com/Dosty-Team/hamroclass",
      repoBackend: "https://github.com/Dosty-Team/hamroclass-backend",
      website: "https://drive.google.com/file/d/1U6JE3-jZOyHJRjcvqgVMB1ZyNhn630Th/view?usp=drive_link"
    },
    {
      title: "Information Management System",
      description: "Solution for businesses. Features include dashboard analytics, product management, category organization, sales tracking, sales reporting, sales prediction, user management, and shareholder distribution. Built to address traditional inventory management challenges by streamlining processes, enhancing accuracy, and providing agile solutions for modern business environments.",
      tech: ["React", "postgres","prisma", "Nestjs","Typescript","Tailwind","JWT","Firebase",],
      image: "/IMS/shareholder_page.png",
      imageAlt: "IMS Dashboard Management",
      gallery: [
        "/IMS/categorypage.png",
        "/IMS/dashboard.png",
        "/IMS/productpage.png",
        "/IMS/salespage.png",
        "/IMS/salesprediction.png",
        "/IMS/salesreport.png",
        "/IMS/shareholder_page.png",
        "/IMS/usermanage.png"
      ],
      repoFrontend: "https://github.com/Dosty-Team/Inventory-Management-System/tree/frontend-v3",
      repoBackend: "https://github.com/Dosty-Team/Inventory-Management-System/tree/backendpart2"
    }
  ];

  return (
    <div className="projects-page">
      {/* Hero Section */}
      <section className="projects-hero">
        <div className="container">
          <div className="projects-hero-content">
            <h1 className="projects-hero-title">
              Featured <span className="highlight">Projects</span>
            </h1>
            <p className="projects-hero-description">
              Here are some of the projects I've worked on, showcasing my skills in full-stack development, 
              UI/UX design, and problem-solving abilities.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="projects-section">
        <div className="container">
          <div className="projects-grid">
            {projects.map((p, i) => (
              <article key={i} className="project-card">
                <div className="project-image">
                  {p.gallery ? (
                    <div 
                      className="clickable-gallery"
                      onClick={() => openImagePopup(p.gallery, p.imageAlt || p.title, 0)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          openImagePopup(p.gallery, p.imageAlt || p.title, 0);
                        }
                      }}
                    >
                      <ProjectGallery images={p.gallery} alt={p.imageAlt || p.title} />
                      <div className="gallery-overlay">
                        <span>Click to view full size</span>
                      </div>
                    </div>
                  ) : (
                    <img src={p.image} alt={p.title} loading="lazy" decoding="async" />
                  )}
                </div>
                <div className="project-content">
                  <h3 className="project-title">{p.title}</h3>
                  <p className="project-description">{p.description}</p>
                  <div className="project-tech">
                    {p.tech.map((t, idx) => (
                      <span key={idx} className="tech-tag">
                        {t || "N/A"}
                      </span>
                    ))}
                  </div>
                  <div className="project-links">
                    <a
                      className="btn btn-outline"
                      href={p.website || '#'}
                      target={p.website ? '_blank' : undefined}
                      rel={p.website ? 'noopener noreferrer' : undefined}
                      aria-label={`Visit ${p.title}`}
                    >
                      View Project
                    </a>
                    {p.repo ? (
                      <a
                        className="btn btn-outline"
                        href={p.repo}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Source code of ${p.title}`}
                      >
                        Source Code
                      </a>
                    ) : null}
                    {p.repoFrontend ? (
                      <a
                        className="btn btn-outline"
                        href={p.repoFrontend}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${p.title} frontend source`}
                      >
                        Frontend Code
                      </a>
                    ) : null}
                    {p.repoBackend ? (
                      <a
                        className="btn btn-outline"
                        href={p.repoBackend}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${p.title} backend source`}
                      >
                        Backend Code
                      </a>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Image Popup Modal */}
      <ImagePopup
        images={popupImages}
        alt={popupAlt}
        isOpen={isPopupOpen}
        onClose={closeImagePopup}
        initialIndex={popupInitialIndex}
      />
    </div>
  );
};

export default Projects;
